/**
 * Web browser equivalent of Expo manipulateAsync for preparing images for AI labeling.
 * Resizes the image to max 800px width/height and compresses to lightweight JPEG base64.
 */
export const compressImageForAI = async (
  fileOrUrl: File | Blob | string,
  maxWidth = 800,
  quality = 0.5
): Promise<{ base64: string; mimeType: string }> => {
  return new Promise((resolve, reject) => {
    let objectUrl = "";
    if (typeof fileOrUrl === "string") {
      objectUrl = fileOrUrl;
    } else {
      objectUrl = URL.createObjectURL(fileOrUrl);
    }

    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxWidth) {
            width = Math.round((width * maxWidth) / height);
            height = maxWidth;
          }
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          throw new Error("Canvas 2D context not available");
        }

        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        const base64 = dataUrl.split(",")[1] || "";

        if (typeof fileOrUrl !== "string") {
          URL.revokeObjectURL(objectUrl);
        }

        resolve({
          base64,
          mimeType: "image/jpeg",
        });
      } catch (err) {
        if (typeof fileOrUrl !== "string") {
          URL.revokeObjectURL(objectUrl);
        }
        reject(err);
      }
    };

    img.onerror = (err) => {
      if (typeof fileOrUrl !== "string") {
        URL.revokeObjectURL(objectUrl);
      }
      reject(err);
    };

    img.src = objectUrl;
  });
};
